import React, { Fragment, useRef, useState } from "react";

import { Box, Button } from "@mui/material";

import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";

import {
    getLeadSalesforceAuthUrl,
    reconnectLeadSalesforceDestination,
} from "helpers/salesforce-lead/salesforce-lead";
import {
    reconnectLeadDestination,
    reconnectLeadSource,
} from "helpers/Sources/reconnect";

import FaceBookSdk from "components/Leads/SharedComponents/FaceBookSdk";
import ZohoComponent from "components/Leads/ZohoComponent";
import LoginButton from "components/molecules/LoginButton";
import TiktokLogin from "components/organisms/SDKs/TiktokLogin";
import SwalAlertComp from "components/SwalAlert";

import { getSingleConnectionRedux } from "../../../redux/slices/leadConnection/leadConnection.asyncActions";
import { ReconnectType } from "./ConnectionDetailReconnectButton.helpers";

const ConnectionDetailReconnectButton = ({
    advertiser,
    appObject,
    language,
    reconnectType,
    destinationId,
}) => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const normalizedReconnectType =
        reconnectType?.toLowerCase() === ReconnectType.SALESFORCE.toLowerCase()
            ? ReconnectType.SALESFORCE
            : reconnectType;

    const facebookButtonRef = useRef();
    const tiktokButtonRef = useRef();

    const [isReconnectInProgress, setIsReconnectInProgress] = useState(false);
    const onLogin = async (loginData) => {
        const handlers = [
            {
                predicate: () => reconnectType === ReconnectType.FACEBOOK,
                handler: (accessToken) =>
                    reconnectLeadSource(id, {
                        access_token: accessToken,
                        platform: "facebook",
                    }),
            },
            {
                predicate: () => reconnectType === ReconnectType.TIKTOK,
                handler: ({ state }) =>
                    reconnectLeadSource(id, {
                        advertiser: advertiser?.toString(),
                        access_token: state,
                        platform: "tiktok",
                    }),
            },
            {
                predicate: () => reconnectType === ReconnectType.ZOHO,
                handler: ({ code, location, accounts_server }) =>
                    reconnectLeadDestination(destinationId, {
                        code,
                        accounts_server,
                        platform: "zoho",
                    }),
            },
            {
                predicate: () =>
                    normalizedReconnectType === ReconnectType.SALESFORCE,
                handler: ({ code, state }) =>
                    reconnectLeadSalesforceDestination(destinationId, {
                        code,
                        state,
                    }),
            },
        ];

        try {
            setIsReconnectInProgress(reconnectType);
            const response = await handlers
                ?.find(({ predicate }) => predicate())
                ?.handler(loginData);

            response?.status &&
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message && response.message,
                    icon: "success",
                    timer: 3000,
                });
            response?.status && dispatch(getSingleConnectionRedux(id));

            !response?.status &&
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
        } catch (error) {
            SwalAlertComp({
                title: "Failed",
                message:
                    "We couldn't reconnect this connection. Please try again.",
                icon: "error",
            });
        } finally {
            setIsReconnectInProgress(false);
        }
    };

    const getSalesforceAuthUrl = async () => {
        const response = await getLeadSalesforceAuthUrl(destinationId);
        if (response?.status) {
            return response?.data?.auth_url ?? null;
        }
        SwalAlertComp({
            title: language?.delete_swal_modal?.failed ?? "Failed",
            message: response?.message,
            icon: "error",
        });
        return null;
    };

    if (reconnectType === ReconnectType.ZOHO) {
        return (
            <Box
                position="relative"
                style={{
                    marginLeft: "20px",
                    marginRight: "20px",
                    marginBottom: "25px",
                }}
            >
                <ZohoComponent
                    isReconnect
                    onReconnect={onLogin}
                    buttonText={
                        isReconnectInProgress ? "Reconnecting" : "Reconnect"
                    }
                    isLoadingExternal={Boolean(isReconnectInProgress)}
                />
            </Box>
        );
    }

    if (normalizedReconnectType === ReconnectType.SALESFORCE) {
        return (
            <Box
                position="relative"
                style={{
                    marginLeft: "20px",
                    marginRight: "20px",
                    marginBottom: "25px",
                }}
            >
                <LoginButton
                    code="salesforce"
                    getAuthUrl={getSalesforceAuthUrl}
                    paramNames={["code", "state"]}
                    onSuccess={onLogin}
                    logoSrc="/assets/images/typeImages/lead/destinations/salesforce.png"
                    buttonText={
                        isReconnectInProgress ? "Reconnecting" : "Reconnect"
                    }
                    isLoadingExternal={Boolean(isReconnectInProgress)}
                />
            </Box>
        );
    }

    return (
        <Fragment>
            <Box
                position="relative"
                width="fit-content"
                style={{ marginLeft: "20px", marginBottom: "25px" }}
            >
                {isReconnectInProgress && (
                    <div className="circle-loader max-height-45">
                        <ClipLoader color={"#fff"} size={17} />
                    </div>
                )}
                <Button
                    color="primary"
                    variant="contained"
                    style={{ width: "fit-content" }}
                    onClick={() => {
                        reconnectType === ReconnectType.FACEBOOK &&
                            facebookButtonRef.current?.click();
                        reconnectType === ReconnectType.TIKTOK &&
                            tiktokButtonRef.current?.click();
                    }}
                >
                    {isReconnectInProgress ? "Reconnecting" : "Reconnect"}
                </Button>
            </Box>
            <div style={{ display: "none" }}>
                {reconnectType === ReconnectType.TIKTOK && (
                    <TiktokLogin
                        ref={tiktokButtonRef}
                        setIsLoading={setIsReconnectInProgress}
                        onLogin={onLogin}
                    />
                )}
                {reconnectType === ReconnectType.FACEBOOK && (
                    <div className="position-relative overflow-hidden z-1 mb-3">
                        <FaceBookSdk
                            ref={facebookButtonRef}
                            updatedData={onLogin}
                            scope="email,pages_manage_ads,ads_management,pages_read_engagement,ads_read,pages_show_list,pages_manage_metadata,leads_retrieval,pages_show_list,pages_manage_metadata,business_management"
                            appId={
                                appObject.app_id && appObject.app_id.toString()
                            }
                            version="v2.9"
                            reRequest={true}
                            setLoaderStatus={setIsReconnectInProgress}
                        />
                    </div>
                )}
            </div>
        </Fragment>
    );
};

export default ConnectionDetailReconnectButton;
